import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Loader2, RotateCcw, Trash2, X } from 'lucide-react';
import type { BackofficeMessageBusDeadLetterAction } from '../types';
import type { BackofficeMessageBusPeekedMessage } from '../types';

export interface ManageDeadLetterMessagesModalProps {
  topicName: string;
  subscriberName: string;
  deadLetterMessageCount: number;
  batchCountInput: string;
  successMessage?: string | null;
  error?: string | null;
  submittingAction?: BackofficeMessageBusDeadLetterAction | null;
  batchCountDisabled?: boolean;
  scope?: 'queue' | 'selected';
  selectedMessages?: BackofficeMessageBusPeekedMessage[];
  warningMessage?: string | null;
  actionCompleted?: boolean;
  onClose: () => void;
  onBatchCountInputChange: (value: string) => void;
  onSubmit: (action: BackofficeMessageBusDeadLetterAction) => Promise<void> | void;
}

export function ManageDeadLetterMessagesModal({
  topicName,
  subscriberName,
  deadLetterMessageCount,
  batchCountInput,
  successMessage = null,
  error = null,
  submittingAction = null,
  batchCountDisabled = false,
  scope = 'queue',
  selectedMessages = [],
  warningMessage = null,
  actionCompleted = false,
  onClose,
  onBatchCountInputChange,
  onSubmit,
}: ManageDeadLetterMessagesModalProps) {
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const submitting = submittingAction !== null;
  const selectedScope = scope === 'selected';
  const actionDisabled = selectedScope
    ? submitting || actionCompleted || selectedMessages.length === 0
    : submitting || batchCountDisabled || deadLetterMessageCount === 0;
  const batchInputId = 'manage-dead-letter-batch-count';

  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !submitting) {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, submitting]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/45 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) {
          onClose();
        }
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="manage-dead-letter-title"
        className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-neutral-200 px-6 py-5">
          <div>
            <h2
              id="manage-dead-letter-title"
              className="font-inter-tight text-xl font-semibold text-neutral-900"
            >
              Manage dead-letter messages
            </h2>
            <p className="mt-1 text-body text-neutral-500">
              {topicName} / {subscriberName}
            </p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="rounded-md p-2 text-neutral-500 hover:bg-backoffice-50 hover:text-neutral-900 disabled:cursor-not-allowed disabled:text-neutral-300"
            aria-label="Close"
            onClick={onClose}
            disabled={submitting}
          >
            <X className="size-5" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <div className="rounded-xl border border-neutral-200 bg-neutral-50 px-4 py-3">
            <p className="text-small font-semibold uppercase tracking-[0.12em] text-neutral-500">
              {selectedScope ? 'Selected dead-letter messages' : 'Current dead-letter queue'}
            </p>
            <p className="mt-2 font-inter-tight text-4xl font-semibold text-neutral-900">
              {selectedScope ? selectedMessages.length : deadLetterMessageCount}
            </p>
            <p className="mt-2 text-body text-neutral-600">
              {selectedScope
                ? `Only the ${selectedMessages.length} selected message${selectedMessages.length === 1 ? '' : 's'} will be processed. All other messages will remain in the dead-letter queue.`
                : 'These messages are currently stuck in the subscriber dead-letter queue.'}
            </p>
            {selectedScope ? (
              <ul className="mt-3 max-h-28 space-y-1 overflow-auto rounded-lg border border-neutral-200 bg-white px-3 py-2 text-small text-neutral-600">
                {selectedMessages.map((message) => (
                  <li key={message.messageKey} className="break-all font-mono">
                    {message.messageId ?? message.sequenceNumber ?? message.messageKey}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          {!selectedScope ? (
            <label
              htmlFor={batchInputId}
              className="flex flex-col gap-2 rounded-xl border border-neutral-200 px-4 py-3 text-body font-semibold text-neutral-900"
            >
              <span>Batch size</span>
              <input
                id={batchInputId}
                type="number"
                inputMode="numeric"
                min="1"
                max={Math.max(deadLetterMessageCount, 1)}
                step="1"
                value={batchCountInput}
                onChange={(event) => onBatchCountInputChange(event.target.value)}
                disabled={submitting}
                className="focus-ring no-tile-drag rounded-md border border-neutral-200 px-3 py-2 text-body font-normal tabular-nums text-neutral-900 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-neutral-400"
                aria-describedby="manage-dead-letter-batch-help"
              />
              <span
                id="manage-dead-letter-batch-help"
                className="text-small font-normal text-neutral-500"
              >
                Process up to this many messages in the next batch.
              </span>
            </label>
          ) : null}

          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-body text-amber-900">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
              <p>
                Re-queue republishes each dead-letter message back onto the same topic, so other
                matching subscribers can receive those messages again.
              </p>
            </div>
          </div>

          {successMessage ? <p className="text-small text-emerald-700">{successMessage}</p> : null}
          {warningMessage ? <p className="text-small text-amber-700">{warningMessage}</p> : null}
          {error ? <p className="text-small text-red-700">{error}</p> : null}

          <div className="grid gap-4 md:grid-cols-2">
            <button
              type="button"
              disabled={actionDisabled}
              onClick={() => void onSubmit('purge')}
              className="focus-ring flex min-h-40 flex-col items-start justify-between rounded-2xl border border-red-200 bg-red-50 p-5 text-left text-red-900 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <div className="space-y-3">
                <Trash2 className="h-5 w-5" aria-hidden="true" />
                <div>
                  <h3 className="font-inter-tight text-lg font-semibold">
                    {selectedScope ? 'Purge selected' : 'Purge dead-letter'}
                  </h3>
                  <p className="mt-2 text-body text-red-800">
                    {selectedScope
                      ? 'Permanently delete only the selected messages from this dead-letter queue.'
                      : 'Permanently delete all messages currently in this dead-letter queue.'}
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center whitespace-nowrap text-small font-semibold">
                {submittingAction === 'purge' ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    &nbsp;Purging messages
                  </>
                ) : selectedScope ? (
                  'Purge selected'
                ) : (
                  'Purge dead-letter'
                )}
              </span>
            </button>

            <button
              type="button"
              disabled={actionDisabled}
              onClick={() => void onSubmit('requeue')}
              className="focus-ring flex min-h-40 flex-col items-start justify-between rounded-2xl border border-backoffice-200 bg-backoffice-50 p-5 text-left text-backoffice-900 transition hover:bg-backoffice-100 disabled:cursor-not-allowed disabled:opacity-70"
            >
              <div className="space-y-3">
                <RotateCcw className="h-5 w-5" aria-hidden="true" />
                <div>
                  <h3 className="font-inter-tight text-lg font-semibold">
                    {selectedScope ? 'Resend selected' : 'Re-queue to topic'}
                  </h3>
                  <p className="mt-2 text-body text-backoffice-800">
                    {selectedScope
                      ? 'Republish only the selected messages to the same topic so normal fan-out runs again.'
                      : 'Republish each dead-letter message to the same topic so normal fan-out runs again.'}
                  </p>
                </div>
              </div>
              <span className="inline-flex items-center whitespace-nowrap text-small font-semibold">
                {submittingAction === 'requeue' ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    &nbsp;{selectedScope ? 'Resending messages' : 'Re-queueing messages'}
                  </>
                ) : selectedScope ? (
                  'Resend selected'
                ) : (
                  'Re-queue to topic'
                )}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
